import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { allTrackers, allData, historylist, resend, allsearch } from './api';

const initialState = {
  all_trackers: [],
  all_data: [],
  history_data: [],
  pagination: null,
  loading: false,
  originalCounts: null // Store original counts to restore when filters are removed
};

const actions = {
  GETTRACKERSLIST: 'getTrackersList/GETTRACKERSLIST',
  GETALLDATA: 'getAllData/GETALLDATA',
  GET_HISTORY: 'getHistoryList/GET_HISTORY',
  RESEND_ENVELOPE: 'resendEnvelope/RESEND_ENVELOPE',
  EXPORT_CSV: 'exportcsv/EXPORT_CSV',
  GETSEARCHLIST: 'getSearchList/GETSEARCHLIST'
};

export const getTrackersList = createAsyncThunk(
  actions.GETTRACKERSLIST,
  async (payload) => {
    const response = await allTrackers('all', payload);
    return response;
  }
);

export const getAllData = createAsyncThunk(
  actions.GETALLDATA,
  async (payload) => {
    const response = await allData('all', payload);
    return response;
  }
);

export const resendEnvelope = createAsyncThunk(
  actions.RESEND_ENVELOPE,
  async (payload) => {
    const response = await resend(`resend?id=${payload}`, payload);
    return response;
  }
);
// getSearchList
export const getSearchList = createAsyncThunk(
  actions.GETSEARCHLIST,
  async (payload) => {
    const response = await allsearch(`all-envelopes`, payload);
    return response;
  }
);

export const getHistoryList = createAsyncThunk(
  actions.GETHISTORYLIST,
  async (payload) => {
    const response = await historylist(`audit-log/${payload}`, payload);
    return response;
  }
);

export const exportcsv = createAsyncThunk(
  actions.EXPORT_CSV,
  async (payload) => {
    const response = await history(`download-csv/${payload}`, payload);
    return response;
  }
);

export const getTrackersSlice = createSlice({
  name: 'trackersList',
  initialState,
  reducers: {
    updatePendingCount: (state, action) => {
      const { count } = action.payload;
      // Update pending count in all_trackers structure
      if (
        state.all_trackers &&
        typeof state.all_trackers === 'object' &&
        state.all_trackers.InitiatedByOthers
      ) {
        state.all_trackers.InitiatedByOthers.pending = count;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTrackersList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTrackersList.fulfilled, (state, action) => {
        const { data, pagination } = action.payload;
        state.all_trackers = data ? data : [];
        state.pagination = pagination;
        state.loading = false;

        // Store original counts when getTrackersList is called (initial load)
        if (data && typeof data === 'object') {
          state.originalCounts = JSON.parse(JSON.stringify(data)); // Deep copy to preserve original counts
        }
      })
      .addCase(getTrackersList.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getAllData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllData.fulfilled, (state, action) => {
        const { data, pagination } = action.payload;
        state.all_data = data ? data : [];
        state.pagination = pagination;
        state.loading = false;
      })
      .addCase(getAllData.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getHistoryList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHistoryList.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.history_data = data ? data : [];
      })
      .addCase(getHistoryList.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(exportcsv.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportcsv.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg);
        }
      })
      .addCase(exportcsv.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });
    builder
      .addCase(resendEnvelope.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendEnvelope.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg);
        }
      })
      .addCase(resendEnvelope.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });
    // getSearchList
    builder
      .addCase(getSearchList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSearchList.fulfilled, (state, action) => {
        const { data, pagination } = action.payload;
        const originalPayload = action.meta?.arg; // Get the original payload

        state.all_data = data ? data : [];
        state.pagination = pagination;
        state.loading = false;

        // Update tab count based on whether filters are applied
        if (pagination?.total_records !== undefined && originalPayload) {
          const hasFilters =
            originalPayload.search ||
            originalPayload.search_cat ||
            originalPayload.from_date ||
            originalPayload.to_date;

          if (state.all_trackers && typeof state.all_trackers === 'object') {
            const { type, category } = originalPayload;

            // Determine which type group (InitiatedByMe or InitiatedByOthers)
            const typeKey =
              type === 'Initiated By Me'
                ? 'InitiatedByMe'
                : 'InitiatedByOthers';

            // Map category to the count field name
            const categoryToFieldMap = {
              pending: 'pending',
              approved: 'approved',
              rejected: 'rejected',
              forwarded: 'forwarded',
              hold: 'hold'
            };

            const countField = categoryToFieldMap[category];

            // Update or restore the count
            if (
              countField &&
              state.all_trackers[typeKey] &&
              state.all_trackers[typeKey][countField] !== undefined
            ) {
              if (hasFilters) {
                // Filters are applied - show filtered count
                state.all_trackers[typeKey][countField] =
                  pagination.total_records;
              } else {
                // Filters are removed - restore original count
                if (
                  state.originalCounts &&
                  state.originalCounts[typeKey] &&
                  state.originalCounts[typeKey][countField] !== undefined
                ) {
                  state.all_trackers[typeKey][countField] =
                    state.originalCounts[typeKey][countField];
                }
              }
            }
          }
        }
      })
      .addCase(getSearchList.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });
  }
});

export const { updatePendingCount } = getTrackersSlice.actions;

export default getTrackersSlice.reducer;
